import { webLog } from '../logging/webLog'

/** Minimal slice of the Owlbert cloud LLM client used for title generation. */
export interface AutoTitleDocLlmClient {
  chatAuto: {
    mutate: (input: {
      messages?: Array<{ role: 'user' | 'assistant' | 'system'; content: string }>
      system_prompt?: string
      temperature?: number
    }) => Promise<{ content?: string | null }>
  }
}

/**
 * Heuristic: does this name look like a raw filename rather than a human title?
 * We only auto-title when the existing name is filename-ish so we never clobber a
 * title a user typed (e.g. via the New note form) or a clean name from extraction.
 */
export function looksLikeFilename(name: string | null | undefined): boolean {
  const t = (name ?? '').trim()
  if (!t) return true
  if (/\.[A-Za-z0-9]{1,5}$/.test(t)) return true // has an extension (report.pdf)
  if (/[_-]{1,}/.test(t) && !/\s/.test(t)) return true // snake/kebab, no spaces (grn_rst_sci)
  if (!/\s/.test(t) && t.length <= 24) return true // single token, no spaces
  if (/^untitled$/i.test(t)) return true
  return false
}

/**
 * Build a holistic sample of a document's text rather than just its opening.
 *
 * Long documents (or chunks of a split document) often start with material that
 * is hyper-specific to that one section, which leads to misleading titles. To
 * capture what the document is *about* overall, we stitch together excerpts from
 * the beginning, several evenly-spaced interior points, and the end.
 */
export function sampleDocumentText(content: string, budget = 4000): string {
  const text = (content ?? '').replace(/\s+/g, ' ').trim()
  if (text.length <= budget) return text

  const segments = 5 // head + 3 interior + tail
  const per = Math.max(400, Math.floor(budget / segments))
  const parts: string[] = []
  for (let i = 0; i < segments; i++) {
    // Evenly distribute window starts across the full length, leaving room for `per`.
    const start = Math.floor((text.length - per) * (i / (segments - 1)))
    parts.push(text.slice(start, start + per).trim())
  }
  // De-dupe overlapping windows (short docs) and join with separators.
  const seen = new Set<string>()
  const unique = parts.filter((p) => p && !seen.has(p) && (seen.add(p), true))
  return unique.join('\n…\n').slice(0, budget + segments * 8)
}

function sanitizeTitle(raw: string): string {
  return raw
    .replace(/^[\s"'`]+|[\s"'`.]+$/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80)
}

/**
 * Generate a concise, human-readable title for an uploaded document from its
 * filename + extracted text. Returns null if a good title can't be produced
 * (caller should keep the original name). Never throws.
 *
 * Example: `grn_rst_sci_doc.pdf` → "Green Rust: Environmental Science Paper".
 */
export async function generateUploadedDocumentTitle(params: {
  llm: AutoTitleDocLlmClient
  filename: string
  content: string
  mimeType?: string | null
}): Promise<string | null> {
  const { llm, filename } = params
  try {
    const snippet = sampleDocumentText(params.content ?? '', 4000)
    if (!snippet) return null

    const reply = await llm.chatAuto.mutate({
      messages: [
        {
          role: 'user',
          content:
            'Create a short, specific, human-readable title for the document below ' +
            '(about 8 words or fewer, max 70 characters). Infer what it is about and, when ' +
            'evident, its type or source (e.g. "Research Paper", "Invoice", "Meeting Notes", ' +
            'a publication or author). The content below is sampled from across the WHOLE ' +
            'document (excerpts separated by "…"), so base the title on its overall subject — ' +
            'do NOT focus on just the opening excerpt. Do NOT just clean up the filename. ' +
            'Reply with ONLY the title text — no quotes, no trailing punctuation, no markdown.\n\n' +
            `Filename: ${filename}\n` +
            (params.mimeType ? `Type: ${params.mimeType}\n` : '') +
            `\nContent:\n${snippet}`,
        },
      ],
      system_prompt:
        'You write concise, accurate document titles. Reply with only the title text.',
      temperature: 0.3,
    })
    const title = sanitizeTitle(reply.content ?? '')
    return title || null
  } catch (e) {
    webLog('warn', 'doc-autotitle', 'llm title generation failed', {
      message: String(e),
      filename,
    })
    return null
  }
}
